import React, { useRef, useEffect, useState } from 'react';
import {
  useLocation,
  useParams,
  useNavigate,
  Navigate,
} from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button } from '@mui/material/';
import Avatar from '@mui/material/Avatar';
import { useAuth } from '../../hooks/useAuth';
import DefaultUserImage from '../../assets/images/default-avatar.png';
import { COURSE_PRICES, STRIPE_PRODUCT_LOOKUP_KEYS } from '../../config/const';
import { fetchResultsByStudentAndCourse, getCourseInfo } from '../../utils/api';
import StripeCheckoutForm from '../StripeCheckoutForm';
import { useTheme } from '@mui/material/styles';

const showCourses = process.env.REACT_APP_SHOW_MY_COURSE === 'true';
const enableDevTools = process.env.REACT_APP_ENABLE_DEVTOOLS === 'true';
const allowTakeCourse = process.env.REACT_APP_ALLOW_TAKE_COURSE === 'true';

const CourseDetails: React.FC = () => {  
  const location = useLocation();
  const { courseId } = useParams();
  const isLoading = useRef<boolean>(true);
  const [courseItem, setCourseItem] = useState(null);
  let [isStripeOpen, setIsStripeOpen] = useState<boolean>(false);
  let [stripeItem, setStripeItem] = useState(null);
  let [stripeItemId, setStripeItemId] = useState(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const navigate = useNavigate();
  const { authUser, authLoading, setLoginModalVisible } = useAuth();
  const theme = useTheme();

useEffect(() => {
  if (showCourses || authUser?.beta_tester) {
    loadCourseData();
    if (authUser?.uid) {
      fetchResultsByStudentAndCourse(authUser?.uid, courseId)
        .then(async (response) => {
          if (!response.ok) {
            throw new Error('Failed to fetch enrollment status');
          }
          const data = await response.json();
          const results = data.data || data;
          
          if (Array.isArray(results)) {
            let enrolledCourses = results.map((result: any) => result.courseId);
            setIsEnrolled(
              enrolledCourses.includes(courseId) ||
                courseItem?.creator_id === authUser?.uid
            );
          } else {
            setIsEnrolled(courseItem?.creator_id === authUser?.uid);
          }
        })
        .catch((err) => {
          console.error('Error fetching enrollment status:', err);
          setIsEnrolled(false);
        });
    }
  }
}, [authUser]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (courseItem && authUser && !isEnrolled) {
      if (courseItem?.creator_id === authUser?.uid) {
        setIsEnrolled(true);
      }
    }
  }, [courseItem, authUser, isEnrolled]);

  useEffect(() => {
    if (isEnrolled) {
      setIsStripeOpen(false);
    }
  }, [isEnrolled]);

  useEffect(() => {
    if (location?.state) {
      setStripeItem(location.state.item);
      setStripeItemId(location.state.item_id);
      setIsStripeOpen(location.state.isStripeOpen);
    }
  }, [location.state]); //eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isStripeOpen) {
      setStripeItem(null);
      setStripeItemId(null);
    }
  }, [isStripeOpen]);

  if (!(showCourses || authUser?.beta_tester || authLoading)) {
    return <Navigate to='/creators' />;
  }

  let loadCourseData = () => {
    console.log("here");
    
    if (location?.state) {
      setStripeItem(location.state.item || null);
      setStripeItemId(location.state.item_id || null);
      setIsStripeOpen(location.state.isStripeOpen || false);
    }
    if (location?.state?.courseItem) {
      setCourseItem(location?.state?.courseItem || null);
    } else {
      // load course data
      isLoading.current = true;
      getCourseInfo(courseId)
        .then((resp) => {
          console.log(resp);
          
          // console.log('API response:', resp); // Log the API response
          isLoading.current = false;
          if (resp.ok) return resp.json();
          throw new Error('Network response was not ok');
        })
        .then((course) => {
          course.docId = courseId;
          // console.log('Course data:', course); // Log the course data
          setCourseItem(course);
        })
        .catch((err) => {
          console.error('Error fetching course info:', err); // Log errors
        });
    }
  };

  let handleEnrollCourse = (event: any, item: any, item_id: any) => {
    event.preventDefault();
    if (authUser?.documentId) {
      setStripeItem(item);
      setStripeItemId(item_id);
      setIsStripeOpen(true);
    } else {
      // if not logged in, force login
      // pass in redirectURL and redirectState
      // use redirectURL to get back here
      // use redirectState to control the state of the popup modal

      setLoginModalVisible({
        isOpen: true,
        formType: 'signin',
        currentStep: 1,
        redirectOptions: {
          redirectURL: `/courses/${courseId}`,
          redirectState: {
            item: item,
            item_id: item_id,
            isStripeOpen: true,
            courseItem: courseItem,
          },
        },
      });
    }
  };

  let handleStartCourse = (event: any, courseId: string) => {
    event.preventDefault();
    navigate(`/learn/${courseId}`);
  };

  const EnrollOrTakeCourse = () => {
    return !isEnrolled ? (
      <Button
        id={`button-enroll-${courseItem.docId}`}
        variant='contained'
        color='primary'
        size='large'
        sx={{
          mr: 4,
        }}
        onClick={(event) =>
          handleEnrollCourse(
            event,
            STRIPE_PRODUCT_LOOKUP_KEYS[
              courseItem?.is_free_course
                ? 'free_course'
                : courseItem.module_count
            ],
            courseItem.docId
          )
        }
      >
        {'Enroll: ' +
          COURSE_PRICES[
            courseItem?.is_free_course
              ? 'free_course'
              : courseItem?.module_count
          ]}
      </Button>
    ) : allowTakeCourse || authUser?.beta_tester ? (
      <Button
        id={`button-take-course-${courseItem.docId}`}
        variant='contained'
        color='primary'
        size='large'
        sx={{
          mr: 4,
        }}
        onClick={(event) => {
          event.preventDefault();
          navigate(`/learn/${courseItem.docId}`);
        }}
      >
        Take Course
      </Button>
    ) : (
      <Button
        id={`button-enrolled-${courseItem.docId}`}
        color='primary'
        variant='contained'
        size='large'
        sx={{
          mr: 4,
        }}
        disabled={true}
      >
        Enrolled
      </Button>
    );
  };

  return courseItem ? (
    <Box display='flex' flex={1}>
      <Box
        width='100%'
        display='flex'
        flexDirection='column'
        alignItems='left'
        textAlign='left'
        sx={{
          mb: 4,
        }}
      >
        <Box
          sx={{
            zIndex: 0,
            backgroundColor: (theme) =>
            theme.palette.background['paper-elevation-6'],
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              zIndex: 1,
              width: '100%',
              maxWidth: 'lg',
              mx: 'auto',
            }}
          >
            <Box
              className={'course-details-creator-box'}
              sx={{
                display: 'flex',
                justifyContent: 'left',
                alignItems: '',
                m: 2,
              }}
            >
              <Avatar
                alt={courseItem?.creator_name}
                src={courseItem?.creator_avatar || DefaultUserImage}
                sx={{
                  width: 60,
                  height: 60,
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  ml: 3,
                }}
              >
                <Typography
                  align='left'
                  variant='h7'
                  color='text.primary'
                  noWrap
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {courseItem.name}
                </Typography>
                <Typography
                  align='left'
                  variant='body2'
                  color='text.secondary'
                  noWrap
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {courseItem.creator_name}
                </Typography>
                <Typography
                  align='left'
                  variant='body2'
                  color='text.secondary'
                  noWrap
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {courseItem.module_count + ' Module Course'}
                </Typography>
              </Box>
            </Box>
            <Box
              className={'course-details-buttons-box'}
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {enableDevTools && (
                <div
                  style={{
                    border: '2px solid #000',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '6px 12px !important',
                    marginRight: 4,
                  }}
                >
                  <p
                    style={{
                      fontSize: '12px',
                      padding: '0px',
                      margin: '0px',
                    }}
                  >
                    Dev Tools for dev and testing only
                  </p>
                  <Button
                    id={`button-take-course-${courseItem.docId}`}
                    className={'course-details-temp-take-course'}
                    variant='contained'
                    sx={{
                      height: '32px !important',
                      borderRadius: '6px',
                      width: '150px',
                    }}
                    fullWidth
                    onClick={(event) => handleStartCourse(event, courseId)}
                  >
                    {'Start Course'}
                  </Button>
                </div>
              )}
              <EnrollOrTakeCourse />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            maxWidth: 'lg',
            mx: 'auto',
            width: '100%',
          }}
        >
          <Box
            className='course-details-description-box'
            sx={{
              mt: 4,
              ml: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Typography align='left' variant='h6' color='text.primary'>
                Subject:
              </Typography>
              <Typography
                align='left'
                variant='body1'
                color='text.secondary'
                sx={{ ml: 1 }}
              >
                {courseItem?.subject}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Typography align='left' variant='h6' color='text.primary'>
                Course:
              </Typography>
              <Typography
                align='left'
                variant='body1'
                color='text.secondary'
                sx={{ ml: 1 }}
              >
                {courseItem?.name}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Typography align='left' variant='h6' color='text.primary'>
                Instructor:
              </Typography>
              <Typography
                align='left'
                variant='body1'
                color='text.secondary'
                sx={{ ml: 1 }}
              >
                {courseItem?.creator_name}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Typography align='left' variant='h6' color='text.primary'>
                Grade:
              </Typography>
              <Typography
                align='left'
                variant='body1'
                color='text.secondary'
                sx={{ ml: 1 }}
              >
                {courseItem?.grade}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Typography align='left' variant='h6' color='text.primary'>
                Duration:
              </Typography>
              <Typography
                align='left'
                variant='body1'
                color='text.secondary'
                sx={{ ml: 1 }}
              >
                {courseItem?.module_count + ' Modules'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <Typography align='left' variant='h6' color='text.primary'>
                Price:
              </Typography>
              <Typography
                align='left'
                variant='body1'
                color='text.secondary'
                sx={{ ml: 1 }}
              >
                {
                  COURSE_PRICES[
                    courseItem?.is_free_course
                      ? 'free_course'
                      : courseItem?.module_count
                  ]
                }
              </Typography>
            </Box>

            <Box
              className={'course-details-overview-box'}
              sx={{ display: 'flex', mt: 4, mb: 2 }}
            >
              <Typography align='left' variant='h6' color='text.primary'>
                Overview:
              </Typography>
              <Typography
                align='left'
                variant='body1'
                color='text.secondary'
                sx={{ ml: 1 }}
              >
                {courseItem?.overview
                  ? courseItem.overview
                  : courseItem.description}
              </Typography>
            </Box>
          </Box>
          {courseItem?.modules.map((mod: any, index: number) => {
            return (
              <Box
                sx={{
                  m: 2,
                }}
                key={`module-${index}`}
              >
                <Typography variant='h6' color='text.primary'>
                  {`Module ${index + 1}: ${mod.title}`}
                </Typography>
                {mod?.topics.map((topic: any, top_index: number) => {
                  return (
                    <Typography
                      variant='body1'
                      color='text.secondary'
                      sx={{
                        ml: 2,
                      }}
                      key={`module-${index}-topic-${top_index}`}
                    >
                      {`${top_index + 1}. ${topic.name}`}
                    </Typography>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Box>
      <StripeCheckoutForm
        item={stripeItem}
        isOpen={isStripeOpen}
        setIsOpen={setIsStripeOpen}
        itemId={stripeItemId}
        redirectURL={`/courses/${courseId}`}
      />
    </Box>
  ) : isLoading.current ? (
    <Box
      display='flex'
      width='100%'
      padding='1.5rem'
      alignItems='center'
      justifyContent='center'
    >
      <CircularProgress size={30} />
    </Box>
  ) : (
    <Box
      mt={2}
      rowGap={2}
      sx={{
        flexDirection: 'column',
        alignItems: 'center',
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
      }}
    >
      <Typography variant='body2' color='text.primary'>
        Course not found
      </Typography>
    </Box>
  );
};

export default CourseDetails;
